import { z } from 'zod';
import {
  AdjustmentSourceSchema,
  CUTOFF_DAYS_BY_EVENT,
  type AdjustmentSource,
  type DayCalendarEntry,
  type EventType,
  type ForecastAdjustment,
} from '@/domain';
import { mockStore } from '@/lib/mock-store';
import { getSupabase, isMockBackend } from '@/lib/supabase';
import { auditService } from './auditService';

const SingleInputSchema = z.object({
  warehouse_id: z.string(),
  customer_id: z.string(),
  date: z.string(), // YYYY-MM-DD
  qty: z.number().int().nonnegative(),
  note: z.string().default(''),
  source: AdjustmentSourceSchema.default('MATRIX_FORM'),
});
export type SingleAdjustmentInput = z.infer<typeof SingleInputSchema>;

export interface SubmitResult {
  inserted: ForecastAdjustment[];
  late: number; // how many flagged is_late
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00`).getTime();
  const b = new Date(`${to}T00:00:00`).getTime();
  return Math.floor((b - a) / 86_400_000);
}

/**
 * Compute is_late given today's date, the target date, and event_type.
 * Cut-off rule: submission must arrive at least N days before target,
 * where N = CUTOFF_DAYS_BY_EVENT[event].
 *
 * Late submissions are still accepted (sets is_late=true).
 */
export function isLate(
  targetDate: string,
  eventType: EventType,
  today: string = todayISO(),
): boolean {
  const cutoff = CUTOFF_DAYS_BY_EVENT[eventType] ?? 1;
  const lead = daysBetween(today, targetDate);
  return lead < cutoff;
}

function randomId(): string {
  return `adj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

async function loadCalendar(): Promise<Map<string, DayCalendarEntry>> {
  const all = isMockBackend
    ? mockStore.getAll('day_calendar')
    : await (async () => {
        const sb = getSupabase()!;
        const { data, error } = await sb.from('day_calendar').select('*');
        if (error) throw error;
        return (data ?? []) as DayCalendarEntry[];
      })();
  return new Map(all.map((c) => [c.date, c]));
}

export const adjustmentService = {
  async list(): Promise<ForecastAdjustment[]> {
    if (isMockBackend) {
      return mockStore
        .getAll('forecast_adjustments')
        .sort((a, b) => b.submitted_at.localeCompare(a.submitted_at));
    }
    const sb = getSupabase()!;
    const { data, error } = await sb
      .from('forecast_adjustments')
      .select('*')
      .order('submitted_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as ForecastAdjustment[];
  },

  /**
   * Bulk submit. Each input becomes a row. Late flag is computed per-row.
   */
  async submitBulk(
    inputs: SingleAdjustmentInput[],
    actor: string,
    source: AdjustmentSource = 'MATRIX_FORM',
  ): Promise<SubmitResult> {
    const calendar = await loadCalendar();
    const today = todayISO();

    const rows: ForecastAdjustment[] = inputs.map((raw) => {
      const parsed = SingleInputSchema.parse(raw);
      const cal = calendar.get(parsed.date);
      const evt = cal?.event_type ?? 'NORMAL';
      return {
        id: randomId(),
        warehouse_id: parsed.warehouse_id,
        customer_id: parsed.customer_id,
        date: parsed.date,
        qty: parsed.qty,
        state: 'NEW',
        source: parsed.source ?? source,
        is_late: isLate(parsed.date, evt, today),
        note: parsed.note ?? '',
        submitted_at: new Date().toISOString(),
        submitted_by: actor,
      };
    });

    if (isMockBackend) {
      const cur = mockStore.getAll('forecast_adjustments');
      mockStore.replace('forecast_adjustments', [...cur, ...rows]);
    } else {
      const sb = getSupabase()!;
      const { error } = await sb.from('forecast_adjustments').insert(rows);
      if (error) throw error;
    }

    const late = rows.filter((r) => r.is_late).length;
    await auditService.log({
      actor,
      action: 'SUBMIT',
      entity_type: 'forecast_adjustments',
      summary: `Gửi ${rows.length} điều chỉnh dự báo (${late} trễ hạn) · nguồn ${source}`,
      payload: {
        count: rows.length,
        late,
        source,
        sample: rows.slice(0, 3).map((r) => ({
          warehouse_id: r.warehouse_id,
          customer_id: r.customer_id,
          date: r.date,
          qty: r.qty,
        })),
      },
    });

    return { inserted: rows, late };
  },

  async submitSingle(input: SingleAdjustmentInput, actor: string): Promise<ForecastAdjustment> {
    const { inserted } = await this.submitBulk([input], actor, input.source ?? 'MATRIX_FORM');
    return inserted[0]!;
  },
};
